import { sharePage } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { useAuthContext } from '@repo/user-session';
import { Button, Control, useToast } from '@zenterprise-inc/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { graphql, useMutation } from 'react-relay';

import { InviteEventActionButtonsPrivacyMutation } from '@/__generated__/InviteEventActionButtonsPrivacyMutation.graphql';
import s from '@/components/event/share/ShareEvent.module.scss';
import { WaitingModal } from '@/components/landing/WaitingModal';
import { INVITE_EVENT_PROMOTION_CODE } from '@/lib/constants/event';
import { INVITE_PROMOTION_PATH } from '@/lib/constants/link';
import { AUTH_PATHS } from '@/lib/constants/paths';
import { useRandomWaitingModal } from '@/lib/hooks/use-random-waiting-modal';
import { useUrlWithQueryString } from '@/lib/hooks/use-url-with-query-string';

const inviteEventActionButtonsPrivacyMutation = graphql`
  mutation InviteEventActionButtonsPrivacyMutation($termsKey: String!) {
    createRefundPromotionTermsLog(termsKey: $termsKey) {
      result
      errors {
        code
        message
      }
    }
  }
`;

export const InviteEventActionButtons = ({ inviteCode, privacyAgreement }: { inviteCode?: string | null; privacyAgreement?: boolean | null }) => {
  const router = useRouter();
  const toast = useToast();
  const { auth } = useAuthContext();
  const { status } = auth;
  const currentUrl = useUrlWithQueryString(INVITE_PROMOTION_PATH, {}, true);
  const signInUrl = useUrlWithQueryString(AUTH_PATHS.로그인, { return_url: currentUrl }, true);
  const [isAgreePrivacy, setIsAgreePrivacy] = useState(false);
  const { sendClickEvent } = useUserEventLogger();
  const [requestAgreement] = useMutation<InviteEventActionButtonsPrivacyMutation>(inviteEventActionButtonsPrivacyMutation);
  const { waitingTime, showWaitingModal, waitingCallback, setWaitingCallback } = useRandomWaitingModal();

  const checkPrivacyAgreement = () => {
    if (!isAgreePrivacy) {
      toast({ content: '개인 정보 수집 및 이용에 동의해주세요.', icon: 'error', iconColor: 'red.600' });
      return false;
    }

    if (!privacyAgreement) {
      requestAgreement({ variables: { termsKey: 'bznav-taxrefund-event' } });
    }

    return true;
  };

  const clickCTAButton = () => {
    sendClickEvent('invite-promo_bottom_invite');
    showWaitingModal();
  };

  const sendKakaoLink = () => {
    if (!checkPrivacyAgreement()) {
      return;
    }

    sendClickEvent('invite-promo_bottom_kakao-share');
    window.Kakao.Share.sendCustom({
      templateId: process.env.NEXT_PUBLIC_ZENV === 'loc' ? 118623 : 110116,
      templateArgs: {
        inviteCode,
        promotionCode: INVITE_EVENT_PROMOTION_CODE
      }
    });
  };

  const clickShared = () => {
    if (!checkPrivacyAgreement()) {
      return;
    }

    const shareLink = `${window.location.origin}/home/event/share/redirect?promotion_code=${INVITE_EVENT_PROMOTION_CODE}&invite_code=${inviteCode}&utm_source=share&utm_campaign=share_friendinvit_promo&utm_medium=organic&utm_content=share_friendinvit_link`;

    sendClickEvent('invite-promo_bottom_link-share');
    sharePage(
      {
        title: '비즈넵 환급 | 수수료 할인받는 친구초대 프로모션 | 사업자라면 바로 참여 가능',
        url: shareLink
      },
      () => {
        toast({ content: '친구 초대 링크를 복사했어요.', icon: 'check-mark', iconColor: 'green.600' });
      }
    );
  };

  useEffect(() => {
    setIsAgreePrivacy(privacyAgreement || false);
  }, [privacyAgreement]);

  useEffect(() => {
    setWaitingCallback(() => () => {
      router.push(signInUrl);
    });
  }, [signInUrl, router, setWaitingCallback]);

  return (
    <div className={'sticky bottom-0 z-20 w-full'}>
      <div className={s.sticky_background} />
      <div className={'flex justify-center bg-white pb-5'}>
        <div className={'px-4.5 w-full max-w-[484px] lg:px-0'}>
          {status === 'logout' && (
            <Button.Box state="primary" size={'large'} fullWidth className={'h-14 py-4'} onClick={clickCTAButton}>
              로그인하고 초대하기
            </Button.Box>
          )}
          {status === 'login' && (
            <div className={'flex w-full flex-col items-center'}>
              <div
                className={
                  'text-16 mb-3 flex cursor-pointer items-center gap-1 bg-white py-1 font-medium leading-[1.43] text-gray-800 underline underline-offset-4'
                }
              >
                <Control.CheckBox
                  onChange={(event) => {
                    setIsAgreePrivacy(event.target.checked);
                  }}
                  defaultChecked={isAgreePrivacy}
                />
                <Link href={'/home/event/share/terms/privacy'} target={'_blank'}>
                  개인 정보 수집 및 이용 동의
                </Link>
              </div>
              <div className={'flex w-full justify-center gap-4'}>
                <Button.Box fullWidth state={'primary'} size={'large'} className={'h-14 py-4'} onClick={clickShared}>
                  링크 공유하기
                </Button.Box>
                <Button.Box fullWidth size={'large'} className={'h-14 bg-[#FEE500] py-4 text-black'} onClick={sendKakaoLink}>
                  카카오톡 초대
                </Button.Box>
              </div>
            </div>
          )}
        </div>
      </div>
      {waitingTime > 0 && <WaitingModal waitingTime={waitingTime} callbackFunction={waitingCallback} />}
    </div>
  );
};
